/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import {
  cities,
  STEPS,
  BUSINESS_TYPE_OPTIONS,
  VENUE_TYPE_OPTIONS,
  VENDOR_TYPE_OPTIONS,
} from "../../../config/appData";
import {
  useAppDispatch,
  useGetBusinessData,
  useVenueData,
  useVenueImageData,
  useVendorProfileData,
  useUserData,
  useMailData,
} from "../../../Utility";
import {
  onBusinessUpdate,
  onBusinessUpdateReset,
  onGetBusiness,
} from "../../../Store/Slices/businessSlice";
import {
  onGetVenue,
  onVenueSave,
  onVenueSaveReset,
} from "../../../Store/Slices/venueSlice";
import {
  onGetVendorProfile,
  onVendorProfileSave,
  onVendorProfileSaveReset,
} from "../../../Store/Slices/vendorProfileSlice";
import {
  onGetImages,
  onVenueImagesUploadReset,
} from "../../../Store/Slices/venueImageSlice";
import {
  setBusinessOnboardingComplete,
  setIsLoggedOut,
} from "../../../Store/Slices/utilitySlice";

import "../../components/RegisterForm/RegisterForm.css";
import "./BusinessCompletionProfile.css";
import { CompletionLoader } from "./BusinessCompletionProfile/CompletionLoader";
import { CompletionHeader } from "./BusinessCompletionProfile/CompletionHeader";
import { CompletionSidebar } from "./BusinessCompletionProfile/CompletionSidebar";
import { CompletionStepsIndicator } from "./BusinessCompletionProfile/CompletionStepsIndicator";
import { CompletionActions } from "./BusinessCompletionProfile/CompletionActions";
import { ProfileCompleteThankYou } from "./BusinessCompletionProfile/ProfileCompleteThankYou";
import Step1BusinessInfo from "./Steps/Step1BusinessInfo";
import Step2VenueDetails from "./Steps/Step2VenueDetails";
import Step3VenueDescription from "./Steps/Step3VenueDescription";
import Step4VenueImages from "./Steps/Step4VenueImages";
import useImageUpload from "./hooks/useImageUpload";
import useStepValidation from "./hooks/useStepValidation";

import { clearLoginDataFunction } from "../../../utils/UtilityFunctions";
import { onSendMail, onSendMailReset } from "../../../Store/Slices/mailSlice";

const cityOptions = cities.map((c) => ({ value: c, label: c }));

function BusinessCompletionProfile({
  embedInProfile = false,
  activeStep: activeStepProp,
}) {
  const navigate = useNavigate();
  const userData = useUserData();
  const dispatch = useAppDispatch();
  const venueData = useVenueData();
  const vendorProfileData = useVendorProfileData();
  const imageData = useVenueImageData();
  const [searchParams] = useSearchParams();
  const cityFromUrl = searchParams.get("city") || "";
  const businessData = useGetBusinessData()?.getBusinessData;
  const businessDataStatus = useGetBusinessData();
  const businessUpdateData = useGetBusinessData();
  const mailData = useMailData();
  const venueExistedAtLoad = useRef(false);
  const vendorExistedAtLoad = useRef(false);
  const step3DataExistedAtLoad = useRef(false);
  const step3VendorDataExistedAtLoad = useRef(false);
  const stepWhenVenueSaveStarted = useRef(null);
  const stepWhenVendorSaveStarted = useRef(null);
  const prevStepRef = useRef(null);
  const submittedProfileCompleteStep4 = useRef(false);
  const pendingThankYouAfterMail = useRef(false);
  const step1Complete = useRef(false);
  const step2Complete = useRef(false);
  const hasSyncedFromBackend = useRef(false);
  const [showProfileCompleteThankYou, setShowProfileCompleteThankYou] =
    useState(false);
  const [isSendingMailAfterFinish, setIsSendingMailAfterFinish] =
    useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [lastCompletedStep, setLastCompletedStep] = useState(0);
  const step3Complete = useRef(false);
  const [formData, setFormData] = useState({
    businessType: "",
    city: "",
    name: "",
    email: "",
    phone: "",
    price_min: "",
    price_max: "",
    venue_type: "",
    capacity_min: "",
    capacity_max: "",
    area_sqft: "",
    number_of_halls: "",
    full_address: "",
    state: "",
    pincode: "",
    description: "",
    amenities: "",
    // Vendor step 2
    willing_to_travel: "",

    years_of_experience: "",
    service_radius_km: "",

    vendor_type: "",
  });
  const [errors, setErrors] = useState({});

  const {
    coverFile,
    setCoverFile,
    galleryFiles,
    setGalleryFiles,
    coverImageId,
    galleryImageIds,
    uploadingSlot,
    setUploadingSlot,
    handleRemoveStep4Image,
    handleConfirmDeleteImage,
    addGallerySlot,
    addGallerySlotsWithFiles,
    validateStep4Images,
    isStep4AllUploaded,
    coverInputRef,
    galleryInputRefs,
    pendingReplaceCoverId,
    pendingReplaceGalleryIds,
    savedImages,
  } = useImageUpload({
    dispatch,
    imageData,
    businessData,
  });
  const {
    validateStep1,
    isStep1DataChanged,
    validateStep2Venue,
    isStep2VenueDataChanged,
    validateStep2Vendor,
    isStep2VendorDataChanged,
    validateStep3Venue,
    isStep3DataChanged,
    isStep3VendorDataChanged,
  } = useStepValidation({
    formData,
    setErrors,
    businessData,
    businessUpdateData,
    venueData,
    vendorProfileData,
    cityFromUrl,
  });
  const totalSteps = STEPS.length;

  // Fetch business data on mount
  useEffect(() => {
    dispatch(onGetBusiness());
    if (businessData?.id) {
      dispatch(onGetImages({ businessId: businessData.id }));
    }
  }, [dispatch]);
  useEffect(() => {
    if (embedInProfile) return;
    if (businessDataStatus?.getBusinessStatusCode === 400) {
      clearLoginDataFunction();
      navigate("/login?type=business");
    } else if (businessData?.status === "approved") {
      dispatch(setBusinessOnboardingComplete(true));
      navigate("/");
    }
  }, [businessDataStatus, embedInProfile, navigate]);

  // Fetch venue or vendor profile and images when business is venue/vendor
  useEffect(() => {
    if (businessData?.business_type === "venue") {
      dispatch(onGetVenue());
    } else if (businessData?.business_type === "vendor") {
      dispatch(onGetVendorProfile());
    }
    if (businessData?.id)
      dispatch(onGetImages({ businessId: businessData.id }));
  }, [businessData?.business_type, businessData?.id, dispatch]);

  useEffect(() => {
    const business = businessUpdateData?.getBusinessData ?? businessData;
    const venue = venueData?.getVenueData;
    const vendorProfile = vendorProfileData?.getProfileData;
    const vendor = vendorProfile?.vendor ?? vendorProfile;
    if (!business) return;
    setFormData((prev) => {
      const next = {
        ...prev,
        businessType: business.business_type,
        city: business.city ?? prev.city,
        name: business.business_name ?? business.name ?? prev.name,
        email: business.email ?? prev.email,
        phone: business.phone ?? prev.phone,
        price_min:
          business.price_min != null
            ? String(business.price_min)
            : prev.price_min,
        price_max:
          business.price_max != null
            ? String(business.price_max)
            : prev.price_max,
      };
      if (venue && business.business_type === "venue") {
        next.venue_type = venue.venue_type ?? prev.venue_type;
        next.capacity_min =
          venue.capacity_min != null
            ? String(venue.capacity_min)
            : prev.capacity_min;
        next.capacity_max =
          venue.capacity_max != null
            ? String(venue.capacity_max)
            : prev.capacity_max;
        next.area_sqft =
          venue.area_sqft != null ? String(venue.area_sqft) : prev.area_sqft;
        next.number_of_halls =
          venue.number_of_halls != null
            ? String(venue.number_of_halls)
            : prev.number_of_halls;
        next.full_address = venue.full_address ?? prev.full_address;
        next.state = venue.state ?? prev.state;
        next.pincode = venue.pincode ?? prev.pincode;
        next.description = venue.description ?? prev.description;
        next.amenities = venue.amenities ?? prev.amenities;
      }
      if (vendor && business.business_type === "vendor") {
        next.full_address = vendor.full_address ?? prev.full_address;
        next.city = vendor.city ?? prev.city;
        next.state = vendor.state ?? prev.state;
        next.pincode = vendor.pincode ?? prev.pincode;
        next.description = vendor.description ?? prev.description;
        next.amenities = vendor.amenities ?? prev.amenities;
        next.willing_to_travel =
          vendor.willing_to_travel ?? prev.willing_to_travel;
        next.vendor_type = vendor.vendor_type ?? prev.vendor_type;
        next.years_of_experience =
          vendor.years_of_experience != null
            ? String(vendor.years_of_experience)
            : prev.years_of_experience;
        next.service_radius_km =
          vendor.service_radius_km != null
            ? String(vendor.service_radius_km)
            : prev.service_radius_km;
      }
      return next;
    });
  }, [
    businessUpdateData?.getBusinessData,
    businessData,
    venueData?.getVenueData,
    vendorProfileData?.getProfileData,
  ]);

  // When navigating to a step, re-sync formData from API
  useEffect(() => {
    const prevStep = prevStepRef.current;
    prevStepRef.current = currentStep;
    const justLanded =
      prevStep !== currentStep && currentStep >= 2 && currentStep <= 4;
    if (!justLanded) return;
    const business = businessUpdateData?.getBusinessData ?? businessData;
    const venue = venueData?.getVenueData;
    const vendorProfile = vendorProfileData?.getProfileData;
    const vendor = vendorProfile?.vendor ?? vendorProfile;
    if (!business) return;
    setFormData((prev) => {
      const next = {
        ...prev,
        businessType: business.business_type === "vendor" ? "vendor" : "venue",
        city: business.city ?? prev.city,
        name: business.business_name ?? business.name ?? prev.name,
        email: business.email ?? prev.email,
        phone: business.phone ?? prev.phone,
        price_min:
          business.price_min != null
            ? String(business.price_min)
            : prev.price_min,
        price_max:
          business.price_max != null
            ? String(business.price_max)
            : prev.price_max,
      };
      if (venue && business.business_type === "venue") {
        next.venue_type = venue.venue_type ?? prev.venue_type;
        next.capacity_min =
          venue.capacity_min != null
            ? String(venue.capacity_min)
            : prev.capacity_min;
        next.capacity_max =
          venue.capacity_max != null
            ? String(venue.capacity_max)
            : prev.capacity_max;
        next.area_sqft =
          venue.area_sqft != null ? String(venue.area_sqft) : prev.area_sqft;
        next.number_of_halls =
          venue.number_of_halls != null
            ? String(venue.number_of_halls)
            : prev.number_of_halls;
        next.full_address = venue.full_address ?? prev.full_address;
        next.state = venue.state ?? prev.state;
        next.pincode = venue.pincode ?? prev.pincode;
        next.description = venue.description ?? prev.description;
        next.amenities = venue.amenities ?? prev.amenities;
      }
      if (vendor && business.business_type === "vendor") {
        next.full_address = vendor.full_address ?? prev.full_address;
        next.state = vendor.state ?? prev.state;
        next.pincode = vendor.pincode ?? prev.pincode;
        next.description = vendor.description ?? prev.description;
        next.amenities = vendor.amenities ?? prev.amenities;
        next.willing_to_travel =
          vendor.willing_to_travel ?? prev.willing_to_travel;
        next.vendor_type = vendor.vendor_type ?? prev.vendor_type;
        next.years_of_experience =
          vendor.years_of_experience != null
            ? String(vendor.years_of_experience)
            : prev.years_of_experience;
        next.service_radius_km =
          vendor.service_radius_km != null
            ? String(vendor.service_radius_km)
            : prev.service_radius_km;
      }
      return next;
    });
  }, [
    currentStep,
    businessUpdateData?.getBusinessData,
    businessData,
    venueData?.getVenueData,
    vendorProfileData?.getProfileData,
  ]);

  // Mark that venue already existed when GET /venues/me returns data
  useEffect(() => {
    if (
      venueData?.getVenueStatusCode === "200" &&
      venueData?.getVenueData?.venue
    ) {
      venueExistedAtLoad.current = true;
    }
  }, [venueData?.getVenueStatusCode, venueData?.getVenueData]);

  // Mark that vendor profile already existed when GET /api/vendors/me returns data
  useEffect(() => {
    if (
      vendorProfileData?.getProfileStatusCode === "200" &&
      vendorProfileData?.getProfileData
    ) {
      vendorExistedAtLoad.current = true;
    }
  }, [
    vendorProfileData?.getProfileStatusCode,
    vendorProfileData?.getProfileData,
  ]);

  // Mark that step 3 (description/amenities) already had data at load – venue
  useEffect(() => {
    const venue = venueData?.getVenueData;
    if (!venue) return;
    const hasDesc = (venue.description ?? "").toString().trim() !== "";
    const hasAmenities = (venue.amenities ?? "").toString().trim() !== "";
    if (hasDesc || hasAmenities) step3DataExistedAtLoad.current = true;
  }, [venueData?.getVenueData]);

  // Mark that step 3 vendor (description/amenities) already had data at load
  useEffect(() => {
    const v =
      vendorProfileData?.getProfileData?.vendor ??
      vendorProfileData?.getProfileData;
    if (!v) return;
    const hasDesc = (v.description ?? "").toString().trim() !== "";
    const hasAmenities = (v.amenities ?? "").toString().trim() !== "";
    if (hasDesc || hasAmenities) step3VendorDataExistedAtLoad.current = true;
  }, [vendorProfileData?.getProfileData]);

  // Pre-fill city when URL has ?city= param
  useEffect(() => {
    if (cityFromUrl && cities.includes(cityFromUrl)) {
      setFormData((prev) => ({ ...prev, city: cityFromUrl }));
    }
  }, [cityFromUrl]);

  // Pre-fill from login/register response (userData) when available
  useEffect(() => {
    if (!businessData) return;
    const role =
      businessData.role ??
      businessData.user_type ??
      businessData.userType ??
      "";
    const businessType =
      role === "vendor" ? "vendor" : role === "venue" ? "venue" : "";
    setFormData((prev) => ({
      ...prev,
      name: businessData.business_name ?? prev.name,
      email: businessData.email ?? prev.email,
      phone: businessData.phone ?? prev.phone,
      city: prev.city || (businessData.city ?? prev.city),
      businessType: prev.businessType || businessType,
    }));
  }, [businessData]);

  // Sync lastCompletedStep and currentStep from backend when business data loads (e.g. on refresh)
  useEffect(() => {
    if (hasSyncedFromBackend.current) return;

    const p = businessData?.profile_step;
    if (p == null) return;

    const step = Number(p);
    if (!Number.isNaN(step)) {
      setLastCompletedStep(step);
      setCurrentStep(step === 4 ? 4 : step + 1);
      hasSyncedFromBackend.current = true;
    }
  }, [businessData?.profile_step]);

  // On refresh: if profile already completed (step 4 / submitted), show thank you.
  // Skip when we're in the Finish flow (waiting for send mail) so thank you only shows after mail API.
  useEffect(() => {
    const step = businessData?.profile_step;
    const status = businessData?.status;

    if (
      step === 4 &&
      (status === "submitted" || status === "created") &&
      !pendingThankYouAfterMail.current
    ) {
      setShowProfileCompleteThankYou(true);
    }
  }, [businessData?.profile_step, businessData?.status]);

  // Handle business update API response
  useEffect(() => {
    const status = businessUpdateData?.status_code;
    if (status === 200 || status === "200") {
      if (submittedProfileCompleteStep4.current) {
        submittedProfileCompleteStep4.current = false;
        pendingThankYouAfterMail.current = true;
        setIsSendingMailAfterFinish(true);
        dispatch(onSendMail({ user_id: userData?.id, type: "newbusiness" }));
      }
      dispatch(onBusinessUpdateReset());
      if (embedInProfile) {
        toast.success("Updated successfully");
        dispatch(onGetBusiness());
      }
      if (!pendingThankYouAfterMail.current) {
        if (step1Complete.current) {
          setCurrentStep((prev) => prev + 1);
          setLastCompletedStep((prev) => Math.max(prev, currentStep));
          step1Complete.current = false;
        } else if (step2Complete.current) {
          setCurrentStep((prev) => prev + 1);
          setLastCompletedStep((prev) => Math.max(prev, currentStep));
          step2Complete.current = false;
        } else if (step3Complete.current) {
          setCurrentStep((prev) => prev + 1);
          setLastCompletedStep((prev) => Math.max(prev, currentStep));
          step3Complete.current = false;
        }
      }
    } else if (status && status !== 200 && status !== "200") {
      if (submittedProfileCompleteStep4.current)
        submittedProfileCompleteStep4.current = false;
      toast.error(businessUpdateData?.message || "Update failed");
      dispatch(onBusinessUpdateReset());
    }
  }, [businessUpdateData?.status_code, businessUpdateData?.message, dispatch]);

  // After send mail API (triggered by Finish flow): show thank you page
  useEffect(() => {
    if (!pendingThankYouAfterMail.current) return;
    const status = mailData?.sendStatusCode;
    const success =
      status === 200 || status === 201 || status === "200" || status === "201";
    const settled = success || mailData?.sendError;
    if (settled) {
      pendingThankYouAfterMail.current = false;
      setIsSendingMailAfterFinish(false);
      dispatch(onSendMailReset());
      dispatch(onGetBusiness());
      dispatch(onGetVenue());
      setShowProfileCompleteThankYou(true);
      setLastCompletedStep((prev) => Math.max(prev, 4));
      if (mailData?.sendError) {
        toast.error(mailData?.sendMessage || "Failed to send notification");
      }
    }
  }, [
    mailData?.sendStatusCode,
    mailData?.sendError,
    mailData?.sendMessage,
    dispatch,
  ]);

  // Handle venue save API response (step 2 / step 3) – 201 created, 200 updated
  // Step 2: call business update profile_step 2 only when venue did not exist at load.
  // Step 3: call business update profile_step 3 only when step 3 data (description/amenities) did not exist at load; if it existed and user only updates, do not call business API.
  useEffect(() => {
    const status = venueData?.saveStatusCode;
    if (
      status === 200 ||
      status === "200" ||
      status === 201 ||
      status === "201"
    ) {
      dispatch(onVenueSaveReset());
      const completed = stepWhenVenueSaveStarted.current ?? 2;
      setLastCompletedStep((prev) => Math.max(prev, completed));

      const shouldCallBusinessApi =
        completed === 2
          ? !venueExistedAtLoad.current
          : !step3DataExistedAtLoad.current;
      if (shouldCallBusinessApi) {
        dispatch(
          onBusinessUpdate({
            id: businessData?.id,
            profile_step: completed,
            business_type: formData.businessType,
          }),
        );
      } else if (step2Complete.current && !shouldCallBusinessApi) {
        setCurrentStep((prev) => prev + 1);
        setLastCompletedStep((prev) => Math.max(prev, currentStep));
        step2Complete.current = false;
      } else if (step3Complete.current && !shouldCallBusinessApi) {
        setCurrentStep((prev) => prev + 1);
        setLastCompletedStep((prev) => Math.max(prev, currentStep));
        step3Complete.current = false;
      }
      dispatch(onGetVenue());
    } else if (venueData?.saveError && status) {
      toast.error(venueData?.saveMessage || "Venue save failed");
      dispatch(onVenueSaveReset());
    }
  }, [
    venueData?.saveStatusCode,
    venueData?.saveError,
    venueData?.saveMessage,
    dispatch,
    businessData?.id,
  ]);

  // Handle vendor profile save API response (step 2 / step 3)
  useEffect(() => {
    const status = vendorProfileData?.saveStatusCode;
    if (
      status === 200 ||
      status === "200" ||
      status === 201 ||
      status === "201"
    ) {
      dispatch(onVendorProfileSaveReset());
      const completed = stepWhenVendorSaveStarted.current ?? 2;
      setLastCompletedStep((prev) => Math.max(prev, completed));

      const shouldCallBusinessApi =
        completed === 2
          ? !vendorExistedAtLoad.current
          : !step3VendorDataExistedAtLoad.current;
      if (shouldCallBusinessApi) {
        dispatch(
          onBusinessUpdate({
            id: businessData?.id,
            profile_step: completed,
            business_type: formData.businessType,
          }),
        );
      } else if (step2Complete.current && !shouldCallBusinessApi) {
        setCurrentStep((prev) => prev + 1);
        setLastCompletedStep((prev) => Math.max(prev, currentStep));
        step2Complete.current = false;
      } else if (step3Complete.current && !shouldCallBusinessApi) {
        setCurrentStep((prev) => prev + 1);
        setLastCompletedStep((prev) => Math.max(prev, currentStep));
        step3Complete.current = false;
      }
      dispatch(onGetVendorProfile());
    } else if (vendorProfileData?.saveError && status) {
      toast.error(
        vendorProfileData?.saveMessage || "Vendor profile save failed",
      );
      dispatch(onVendorProfileSaveReset());
    }
  }, [
    vendorProfileData?.saveStatusCode,
    vendorProfileData?.saveError,
    vendorProfileData?.saveMessage,
    dispatch,
    businessData?.id,
  ]);

  // Handle venue images upload response (step 4 batch): if venue was just created, call business update with profile_completed; then finish
  useEffect(() => {
    const status = imageData?.imagesStatusCode;
    if (status === 200 || status === "200") {
      dispatch(onVenueImagesUploadReset());
      if (!venueExistedAtLoad.current) {
        dispatch(
          onBusinessUpdate({
            id: businessData?.id,
            business_type: formData.businessType,
            profile_step: 4,
          }),
        );
      }
      dispatch(setBusinessOnboardingComplete(true));
      navigate("/");
    } else if (imageData?.imagesError && status) {
      toast.error(imageData?.imagesMessage || "Image upload failed");
      dispatch(onVenueImagesUploadReset());
    }
  }, [
    imageData?.imagesStatusCode,
    imageData?.imagesError,
    imageData?.imagesMessage,
    dispatch,
    navigate,
    businessData?.id,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (name === "price_min" && errors.price_max)
      setErrors((prev) => ({ ...prev, price_max: "" }));
    if (name === "capacity_min" && errors.capacity_max)
      setErrors((prev) => ({ ...prev, capacity_max: "" }));
  };
  const status = businessData?.status;
  const handleNext = () => {
    ;
    if (currentStep === 1) {
      if (!validateStep1()) return;
      if (!isStep1DataChanged()) {
        setLastCompletedStep((prev) => Math.max(prev, 1));
        setCurrentStep((s) => s + 1);
        return;
      }
      dispatch(
        onBusinessUpdate({
          id: businessData?.id,
          profile_step: currentStep,
          business_type: formData.businessType,
          city: formData.city,
          business_name: formData.name,
          email: formData.email,
          phone: formData.phone,
          price_min: formData.price_min,
          price_max: formData.price_max,
        }),
      );
      step1Complete.current = true;
      return;
    }
    if (currentStep === 2 && formData.businessType === "venue") {
      if (!validateStep2Venue()) return;
      if (!isStep2VenueDataChanged()) {
        setLastCompletedStep((prev) => Math.max(prev, 2));
        setCurrentStep((s) => s + 1);
        return;
      }
      if (formData.city?.trim() && formData.city !== businessData?.city) {
        dispatch(
          onBusinessUpdate({
            id: businessData?.id,
            city: formData.city.trim(),
            business_type: formData.businessType,
          }),
        );
      }
      stepWhenVenueSaveStarted.current = 2;
      const venuePayload = buildVenuePayload("");
      dispatch(onVenueSave(venuePayload));
      step2Complete.current = true;
      return;
    }
    if (currentStep === 2 && formData.businessType === "vendor") {
      if (!validateStep2Vendor()) return;
      if (!isStep2VendorDataChanged()) {
        setLastCompletedStep((prev) => Math.max(prev, 2));
        setCurrentStep((s) => s + 1);
        return;
      }
      if (formData.city?.trim() && formData.city !== businessData?.city) {
        dispatch(
          onBusinessUpdate({
            id: businessData?.id,
            city: formData.city.trim(),
            business_type: formData.businessType,
          }),
        );
      }
      stepWhenVendorSaveStarted.current = 2;
      dispatch(onVendorProfileSave(buildVendorPayload(2)));
      step2Complete.current = true;
      return;
    }
    if (currentStep === 3 && formData.businessType === "venue") {
      if (!validateStep3Venue()) return;
      if (!isStep3DataChanged()) {
        setLastCompletedStep((prev) => Math.max(prev, 3));
        setCurrentStep((s) => s + 1);
        return;
      }
      stepWhenVenueSaveStarted.current = 3;
      const venuePayload = buildVenuePayload("step3");
      dispatch(onVenueSave(venuePayload));
      step3Complete.current = true;
      return;
    }
    if (currentStep === 3 && formData.businessType === "vendor") {
      if (!validateStep3Venue()) return;
      if (!isStep3VendorDataChanged()) {
        setLastCompletedStep((prev) => Math.max(prev, 3));
        setCurrentStep((s) => s + 1);
        return;
      }
      stepWhenVendorSaveStarted.current = 3;
      dispatch(onVendorProfileSave(buildVendorPayload(3)));
      step3Complete.current = true;
      return;
    }
    if (currentStep === 4) {
      if (!validateStep4Images(setErrors)) return;
      if (isStep4AllUploaded()) {
        submittedProfileCompleteStep4.current = true;
        dispatch(
          onBusinessUpdate({
            id: businessData?.id,
            business_type: formData.businessType,
            ...(status === "created" && { profile_step: 4 }),
            ...(status === "created" && { status: "submitted" }),
          }),
        );
      }
      return;
    }
  };

  /** Vendor profile API payload – step 2 create (no id), step 3 update (id required) */
  function buildVendorPayload(step) {
    const vendorProfile = vendorProfileData?.getProfileData;
    const vendor = vendorProfile?.vendor ?? vendorProfile;
    const vendorId = vendor?.id ?? vendor?.vendor_id;
    const hasExistingId =
      vendorId != null &&
      vendorId !== "" &&
      (Number(vendorId) || vendorId) !== 0;

    const payload = {
      business_id: Number(businessData?.id) || 0,
      full_address: formData.full_address?.trim() ?? "",
      city: formData.city?.trim() ?? "",
      state: formData.state?.trim() ?? "",
      pincode: formData.pincode?.trim() ?? "",

      willing_to_travel: formData.willing_to_travel?.trim() ?? "",
      years_of_experience: Number(formData.years_of_experience) || 0,
      service_radius_km: Number(formData.service_radius_km) || 0,
      vendor_type: formData.vendor_type?.trim() ?? "",
      description: formData.description?.trim() ?? "",
      amenities: formData.amenities?.trim() ?? "",
    };
    // Step 2: send id only when updating existing record; omit when creating
    // Step 3: always send id (we only update, never create in step 3)
    if (step === 3 && hasExistingId) payload.id = Number(vendorId) || vendorId;
    else if (step === 2 && hasExistingId)
      payload.id = Number(vendorId) || vendorId;
    return payload;
  }

  function buildVenuePayload(step) {
    const venue = venueData?.getVenueData;
    const venueId = venue?.id ?? venue?.venue_id;
    const isVendor = formData.businessType === "vendor";

    if (isVendor) {
      return buildVendorPayload(step === "step3" ? 3 : 2);
    }

    const payload = {
      business_id: Number(businessData?.id),
      venue_type: formData.venue_type,
      capacity_min: Number(formData.capacity_min) || 0,
      capacity_max: Number(formData.capacity_max) || 0,
      number_of_halls: Number(formData.number_of_halls) || 0,
      full_address: formData.full_address?.trim() ?? "",
      city: formData.city?.trim() ?? "",
      state: formData.state?.trim() ?? "",
      pincode: formData.pincode?.trim() ?? "",
      description: step === "step3" ? (formData.description?.trim() ?? "") : "",
      amenities:
        step === "step3"
          ? formData.amenities?.trim()
            ? formData.amenities
                .split(",")
                .map((s) => `'${s.trim().replace(/'/g, "''")}'`)
                .join(",")
            : ""
          : "",
    };
    if (formData.area_sqft !== "" && formData.area_sqft != null)
      payload.area_sqft = Number(formData.area_sqft) || 0;
    if (venueId != null && venueId !== "")
      payload.id = Number(venueId) || venueId;
    return payload;
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((s) => s - 1);
    } else {
      navigate("/");
    }
  };

  const goToStep = (stepId) => {
    if (showProfileCompleteThankYou) setShowProfileCompleteThankYou(false);
    setCurrentStep(stepId);
    setErrors({});
  };

  /** Save current step only (for embed profile – no step advance). */
  const handleSaveForStep = (stepNum) => {
    if (stepNum === 1) {
      if (!validateStep1()) return;
      dispatch(
        onBusinessUpdate({
          id: businessData?.id,
          profile_step: 1,
          business_type: formData.businessType,
          city: formData.city,
          business_name: formData.name,
          email: formData.email,
          phone: formData.phone,
          price_min: formData.price_min,
          price_max: formData.price_max,
        }),
      );

      return;
    }
    if (stepNum === 2 && formData.businessType === "venue") {
      if (!validateStep2Venue()) return;
      if (formData.city?.trim() && formData.city !== businessData?.city) {
        dispatch(
          onBusinessUpdate({
            id: businessData?.id,
            city: formData.city.trim(),
            business_type: formData.businessType,
          }),
        );
      }
      stepWhenVenueSaveStarted.current = 2;
      dispatch(onVenueSave(buildVenuePayload("")));

      return;
    }
    if (stepNum === 2 && formData.businessType === "vendor") {
      if (!validateStep2Vendor()) return;
      if (formData.city?.trim() && formData.city !== businessData?.city) {
        dispatch(
          onBusinessUpdate({
            id: businessData?.id,
            city: formData.city.trim(),
            business_type: formData.businessType,
          }),
        );
      }
      stepWhenVendorSaveStarted.current = 2;
      dispatch(onVendorProfileSave(buildVendorPayload(2)));

      return;
    }
    if (stepNum === 3 && formData.businessType === "venue") {
      if (!validateStep3Venue()) return;
      stepWhenVenueSaveStarted.current = 3;
      dispatch(onVenueSave(buildVenuePayload("step3")));

      return;
    }
    if (stepNum === 3 && formData.businessType === "vendor") {
      if (!validateStep3Venue()) return;
      stepWhenVendorSaveStarted.current = 3;
      dispatch(onVendorProfileSave(buildVendorPayload(3)));

      return;
    }
  };

  const isVenueFlow =
    formData.businessType === "venue" ||
    businessData?.business_type === "venue";
  const safeStep = Math.min(totalSteps, Math.max(1, Number(currentStep) || 1));
  const isLoading =
    businessUpdateData?.isLoading ||
    venueData?.saveLoading ||
    vendorProfileData?.saveLoading ||
    imageData?.imagesLoading ||
    imageData?.singleImageLoading ||
    mailData?.sendLoading;
  const pageLoading =
    businessUpdateData?.getBusinessLoading ||
    imageData?.getImagesLoading ||
    (businessData?.business_type === "venue" && venueData?.getVenueLoading) ||
    (businessData?.business_type === "vendor" &&
      vendorProfileData?.getProfileLoading);

  if (embedInProfile) {
    const stepToShow = Math.min(4, Math.max(1, Number(activeStepProp) || 1));
    const hasEmbedStepDataChanged =
      stepToShow === 1
        ? isStep1DataChanged()
        : stepToShow === 2
          ? isVenueFlow
            ? isStep2VenueDataChanged()
            : isStep2VendorDataChanged()
          : stepToShow === 3
            ? isVenueFlow
              ? isStep3DataChanged()
              : isStep3VendorDataChanged()
            : false;
    return (
      <div className="business-completion-embed">
        <div className="profile-business-content">
          <div className="step-content-card profile-step-card">
            {pageLoading ? (
              <CompletionLoader show />
            ) : (
              <>
                <h3 className="profile-section-title">
                  {STEPS[stepToShow - 1]?.title}
                </h3>
                {stepToShow === 1 && (
                  <Step1BusinessInfo
                    formData={formData}
                    errors={errors}
                    handleChange={handleChange}
                    cityOptions={cityOptions}
                    BUSINESS_TYPE_OPTIONS={BUSINESS_TYPE_OPTIONS}
                    readOnly={false}
                    embedInProfile={embedInProfile}
                  />
                )}
                {stepToShow === 2 && (
                  <Step2VenueDetails
                    formData={formData}
                    errors={errors}
                    handleChange={handleChange}
                    cityOptions={cityOptions}
                    VENUE_TYPE_OPTIONS={VENUE_TYPE_OPTIONS}
                    VENDOR_TYPE_OPTIONS={VENDOR_TYPE_OPTIONS}
                    isVenueFlow={isVenueFlow}
                    readOnly={false}
                  />
                )}
                {stepToShow === 3 && (
                  <Step3VenueDescription
                    formData={formData}
                    errors={errors}
                    handleChange={handleChange}
                    readOnly={false}
                    isVendor={formData.businessType === "vendor"}
                  />
                )}
                {stepToShow === 4 && (
                  <Step4VenueImages
                    coverFile={coverFile}
                    setErrors={setErrors}
                    coverImageId={coverImageId}
                    galleryFiles={galleryFiles}
                    galleryImageIds={galleryImageIds}
                    uploadingSlot={uploadingSlot}
                    errors={errors}
                    savedImages={savedImages}
                    coverInputRef={coverInputRef}
                    galleryInputRefs={galleryInputRefs}
                    handleRemoveStep4Image={handleRemoveStep4Image}
                    handleConfirmDeleteImage={handleConfirmDeleteImage}
                    addGallerySlot={addGallerySlot}
                    addGallerySlotsWithFiles={addGallerySlotsWithFiles}
                    setCoverFile={setCoverFile}
                    setGalleryFiles={setGalleryFiles}
                    setUploadingSlot={setUploadingSlot}
                    dispatch={dispatch}
                    businessData={businessData}
                    pendingReplaceCoverId={pendingReplaceCoverId}
                    pendingReplaceGalleryIds={pendingReplaceGalleryIds}
                    readOnly={false}
                  />
                )}

                {(stepToShow === 1 || stepToShow === 2 || stepToShow === 3) && (
                  <div className="profile-embed-actions">
                    <button
                      type="button"
                      className="profile-save-btn"
                      onClick={() => handleSaveForStep(stepToShow)}
                      disabled={!hasEmbedStepDataChanged || isLoading}
                    >
                      {isLoading ? "Updating..." : "Update Info"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="business-completion-profile">
      <CompletionHeader
        onLogoClick={() => navigate("/profile/business", { replace: true })}
        onLogout={() => {
          clearLoginDataFunction();
          dispatch(setIsLoggedOut());
        }}
        showSteps={showProfileCompleteThankYou}
        showFinish={businessData?.status === "submitted" && currentStep === 4}
      />
      <div className="business-completion-container">
        <div className="business-completion-body">
          {showProfileCompleteThankYou ? null : (
            <CompletionSidebar
              steps={STEPS}
              currentStep={currentStep}
              lastCompletedStep={lastCompletedStep}
              onGoToStep={goToStep}
            />
          )}
          <div className="business-completion-main">
            {showProfileCompleteThankYou ? null : (
              <CompletionStepsIndicator
                steps={STEPS}
                currentStep={currentStep}
              />
            )}

            <div className="business-completion-content">
              {showProfileCompleteThankYou ? (
                <ProfileCompleteThankYou
                  onGoToDashboard={() => {
                    dispatch(setBusinessOnboardingComplete(true));
                    navigate("/");
                  }}
                  onViewProfile={() => setShowProfileCompleteThankYou(false)}
                />
              ) : pageLoading ? (
                <CompletionLoader show />
              ) : (
                <div className="step-content-card">
                  <h2 className="step-content-title">
                    {STEPS[safeStep - 1]?.title}
                  </h2>
                  {safeStep === 1 && (
                    <Step1BusinessInfo
                      formData={formData}
                      errors={errors}
                      handleChange={handleChange}
                      cityOptions={cityOptions}
                      BUSINESS_TYPE_OPTIONS={BUSINESS_TYPE_OPTIONS}
                    />
                  )}
                  {safeStep === 2 && (
                    <Step2VenueDetails
                      formData={formData}
                      isVenueFlow={isVenueFlow}
                      errors={errors}
                      handleChange={handleChange}
                      cityOptions={cityOptions}
                      VENUE_TYPE_OPTIONS={VENUE_TYPE_OPTIONS}
                      VENDOR_TYPE_OPTIONS={VENDOR_TYPE_OPTIONS}
                    />
                  )}

                  {safeStep === 3 && (
                    <Step3VenueDescription
                      formData={formData}
                      errors={errors}
                      handleChange={handleChange}
                      isVendor={formData.businessType === "vendor"}
                    />
                  )}
                  {safeStep === 4 && (
                    <Step4VenueImages
                      coverFile={coverFile}
                      setErrors={setErrors}
                      coverImageId={coverImageId}
                      galleryFiles={galleryFiles}
                      galleryImageIds={galleryImageIds}
                      uploadingSlot={uploadingSlot}
                      errors={errors}
                      savedImages={savedImages}
                      coverInputRef={coverInputRef}
                      galleryInputRefs={galleryInputRefs}
                      handleRemoveStep4Image={handleRemoveStep4Image}
                      handleConfirmDeleteImage={handleConfirmDeleteImage}
                      addGallerySlot={addGallerySlot}
                      addGallerySlotsWithFiles={addGallerySlotsWithFiles}
                      setCoverFile={setCoverFile}
                      setGalleryFiles={setGalleryFiles}
                      setUploadingSlot={setUploadingSlot}
                      dispatch={dispatch}
                      businessData={businessData}
                      pendingReplaceCoverId={pendingReplaceCoverId}
                      pendingReplaceGalleryIds={pendingReplaceGalleryIds}
                    />
                  )}
                </div>
              )}
            </div>

            {!showProfileCompleteThankYou && (
              <CompletionActions
                currentStep={currentStep}
                totalSteps={totalSteps}
                isLoading={isLoading}
                loadingLabel={
                  isSendingMailAfterFinish && mailData?.sendLoading
                    ? "Saving..."
                    : imageData?.singleImageLoading &&
                        imageData?.singleImageIsDelete
                      ? "Deleting..."
                      : imageData?.imagesLoading
                        ? "Saving..."
                        : undefined
                }
                onBack={handleBack}
                hideFinish={
                  businessData?.status === "submitted" &&
                  currentStep === 4 &&
                  !isSendingMailAfterFinish
                }
                disableNext={
                  (currentStep === 4 && isVenueFlow && !isStep4AllUploaded()) ||
                  (currentStep === 4 &&
                    (imageData?.imagesLoading ||
                      imageData?.singleImageLoading ||
                      imageData?.getImagesLoading))
                }
                onNext={handleNext}
              />
            )}
      
          
          </div>
        </div>
      </div>
    </div>
  );
}

export default BusinessCompletionProfile;
